import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { timeAgo } from "@/lib/format";
import { PageLoader, EmptyState } from "@/components/common";
import { Activity, LifeBuoy, Flag, BadgeCheck, RotateCcw, UserX } from "lucide-react";

const KIND = {
  support: { icon: LifeBuoy, color: "text-sky-600" },
  report: { icon: Flag, color: "text-red-600" },
  verify: { icon: BadgeCheck, color: "text-price" },
  relist: { icon: RotateCcw, color: "text-amber-600" },
  deletion: { icon: UserX, color: "text-destructive" },
};

export function AdminActivity() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ["admin-activity"], queryFn: () => apiFetch("/admin/activity"), refetchInterval: 20000 });
  if (isLoading) return <PageLoader />;
  const { counts, items } = data;

  return (
    <div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        {[
          { k: "support", label: "Support" },
          { k: "reports", label: "Reports" },
          { k: "verify", label: "Verify" },
          { k: "relist", label: "Relist" },
          { k: "deletion", label: "Deletions" },
        ].map((c) => (
          <div key={c.k} className="rounded-2xl border border-border p-4 text-center">
            <p className="text-2xl font-extrabold text-brand">{counts[c.k] || 0}</p>
            <p className="text-xs text-muted-foreground">{c.label}</p>
          </div>
        ))}
      </div>

      <h2 className="mt-6 font-semibold">Pending queue</h2>
      <div className="mt-3 space-y-2">
        {items.length === 0 ? (
          <EmptyState icon={Activity} title="All clear" message="No pending items right now." />
        ) : (
          items.map((it) => {
            const meta = KIND[it.kind] || {};
            const Icon = meta.icon || Activity;
            return (
              <div key={`${it.kind}-${it.id}`} className="flex items-start gap-3 rounded-2xl border border-border p-3" data-testid={`activity-${it.id}`}>
                <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${meta.color || "text-muted-foreground"}`} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{it.title}</p>
                  <p className="truncate text-xs text-muted-foreground">{it.subtitle}</p>
                </div>
                <span className="shrink-0 text-xs text-muted-foreground">{timeAgo(it.created_at)}</span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
